const MAX_PAYLOAD_BYTES = 32 * 1024 * 1024;
const PROVIDER_HOSTS = new Set(["app.quotemedia.com", "quotes.quotemedia.com", "uat.quotemedia.com"]);

export class QuoteMediaBrowserOptionsClient {
  constructor({ fetch: fetchImplementation = globalThis.fetch } = {}) {
    this.fetch = fetchImplementation.bind(globalThis);
  }

  async chain(grant, { expiration = null, side = "Both", money = "all", signal } = {}) {
    const parameters = { symbol: grant.underlying.instrument.providerSymbol,
      excode: "oprac", expire: "all", callput: side === "Both" ? "all" : side.toLowerCase(),
      money, inclExpired: "false", inclZeroVol: "true", page: "0", greeks: "true", timezone: "true" };
    if (expiration) Object.assign(parameters, { expireMin: expiration, expireMax: expiration });
    return parseOptionChain(await this.request(grant, grant.chainEndpoint, parameters, signal), grant);
  }

  async quotes(grant, symbols, signal) {
    const quotes = [];
    for (const batch of optionSymbolBatches(symbols)) {
      const payload = await this.request(grant, grant.quotesEndpoint,
        { symbols: batch.join(","), greeks: "true", markInd: "true", timezone: "true" }, signal);
      const requested = new Set(batch.map(symbol => symbol.toUpperCase()));
      quotes.push(...parseOptionQuotes(payload, grant.availability)
        .filter(item => requested.has(item.symbol.toUpperCase())));
    }
    return quotes;
  }

  async underlyingQuote(grant, { signal } = {}) {
    validateOptionBrowserGrant(grant);
    // Use the same approved provider environment and end-user session, not
    // getOptions (which is reserved for the selected option contracts).
    const endpoint = new URL("/data/getSnapQuotes.json", grant.chainEndpoint).href;
    const payload = await this.request(grant, endpoint,
      { symbols: grant.underlying.instrument.providerSymbol, timezone: "true" }, signal);
    return parseUnderlyingQuote(payload, grant);
  }

  async request(grant, endpoint, parameters, signal) {
    validateOptionBrowserGrant(grant);
    const url = new URL(endpoint);
    for (const [key, value] of Object.entries(parameters)) url.searchParams.set(key, value);
    url.searchParams.set("webmasterId", grant.webmasterId);
    url.searchParams.set("sid", grant.sid);
    if (grant.availability === "Delayed") url.searchParams.set("entmax", "DL");
    if (grant.availability === "Historical") url.searchParams.set("entmax", "EOD");
    try {
      const response = await this.fetch(url.href, {
        method: "GET", mode: "cors", credentials: "omit", cache: "no-store",
        referrerPolicy: "no-referrer", headers: { Accept: "application/json" },
        signal: AbortSignal.any([...(signal ? [signal] : []), AbortSignal.timeout(20000)])
      });
      if (!response.ok) throw optionError("QuoteMedia options request failed.", response.status,
        [401, 403].includes(response.status) ? "provider_auth_rejected" : null);
      if (Number(response.headers?.get("content-length")) > MAX_PAYLOAD_BYTES) throw invalidPayload();
      let text;
      if (response.body?.getReader) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        const chunks = [];
        let bytes = 0;
        try {
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            bytes += value.byteLength;
            if (bytes > MAX_PAYLOAD_BYTES) { await reader.cancel(); throw invalidPayload(); }
            chunks.push(decoder.decode(value, { stream: true }));
          }
          chunks.push(decoder.decode());
          text = chunks.join("");
        } finally { reader.releaseLock(); }
      } else {
        text = await response.text();
        if (new TextEncoder().encode(text).byteLength > MAX_PAYLOAD_BYTES) throw invalidPayload();
      }
      return JSON.parse(text);
    } catch (error) {
      if (signal?.aborted) throw signal.reason || new DOMException("Aborted", "AbortError");
      if (error?.name === "OptionChainRequestError") throw error;
      if (error instanceof TypeError) {
        throw optionError("QuoteMedia browser access is unavailable. Check cross-origin access and your market-data session.",
          0, "provider_browser_unavailable");
      }
      // Never forward provider URLs, SID values, or provider error text to the UI.
      throw invalidPayload();
    }
  }
}

export function validateOptionBrowserGrant(grant) {
  if (grant?.transport !== "Browser" || !grant.sid || !grant.webmasterId ||
      !grant.underlying?.instrument?.providerSymbol ||
      !["RealTime", "Delayed", "Historical"].includes(grant.availability) ||
      !Number.isFinite(Date.parse(grant.expiresAtUtc)) || Date.parse(grant.expiresAtUtc) <= Date.now()) {
    throw optionError("Options authorization needs to be renewed.", 401);
  }
  for (const key of ["password", "username", "enterpriseToken", "applicationSecret", "contractIdentitySecret"]) {
    if (Object.hasOwn(grant, key)) throw invalidPayload();
  }
  for (const [key, path] of [["chainEndpoint", "/data/getOptionChain.json"], ["quotesEndpoint", "/data/getOptions.json"]]) {
    let url;
    try { url = new URL(grant[key]); } catch { throw invalidPayload(); }
    if (url.protocol !== "https:" || !PROVIDER_HOSTS.has(url.hostname) || url.port ||
        url.username || url.password || url.search || url.hash || url.pathname !== path) throw invalidPayload();
  }
  if ((new URL(grant.chainEndpoint).hostname === "uat.quotemedia.com") !==
      (new URL(grant.quotesEndpoint).hostname === "uat.quotemedia.com")) throw invalidPayload();
}

export function optionSymbolBatches(symbols) {
  const batches = [];
  let batch = [];
  let length = 0;
  for (const symbol of new Set(symbols.map(value => String(value).trim()))) {
    if (!symbol || symbol.length > 1000 || symbol.includes(",")) throw invalidPayload();
    if (batch.length === 30 || length + symbol.length + (batch.length ? 1 : 0) > 1000) {
      batches.push(batch); batch = []; length = 0;
    }
    length += symbol.length + (batch.length ? 1 : 0);
    batch.push(symbol);
  }
  if (batch.length) batches.push(batch);
  return batches;
}

export function parseOptionChain(payload, grant) {
  const results = resultsOf(payload);
  const chain = get(results, "optionchain");
  checkProviderError(chain);
  checkEntitlement(get(chain, "entitlement"), grant.availability);
  const requested = grant.underlying.instrument.providerSymbol;
  if (symbolOf(chain) && !sameSymbol(symbolOf(chain), requested)) throw invalidPayload();
  const contracts = parseOptionQuotes(payload, grant.availability);
  if (contracts.some(item => item.underlyingSymbol && !sameSymbol(item.underlyingSymbol, requested))) throw invalidPayload();
  const root = get(chain, "pricedata") ? chain : quoteElements(results).map(item => get(item, "root"))
    .find(item => get(item, "pricedata") && sameSymbol(symbolOf(item), requested));
  const price = get(root, "pricedata");
  checkEntitlement(get(root, "entitlement"), grant.availability);
  return { contracts: [...new Map(contracts.map(item => [item.symbol, item])).values()],
    underlying: { ...grant.underlying, last: numberOf(price, "last"), change: numberOf(price, "change"),
      changePercent: numberOf(price, "changepercent"),
      quoteTimestampUtc: timestampOf(price, "lastquotedatetime") || timestampOf(root, "datetime"),
      quoteState: numberOf(price, "last") === null ? "Unavailable" : stateOf(get(root, "entitlement")) } };
}

export function parseOptionQuotes(payload, availability) {
  return quoteElements(resultsOf(payload)).filter(item => item !== null).map(item => {
    if (!item || typeof item !== "object" || Array.isArray(item)) throw invalidPayload();
    checkProviderError(item);
    const contract = get(item, "contract") || item;
    const price = get(item, "pricedata") || item;
    const root = get(item, "root");
    const greeksSource = get(item, "greeks");
    const symbol = symbolOf(item);
    const expiration = get(contract, "expirydate");
    const strike = numberOf(contract, "strike");
    const right = /^(call|c)$/i.test(get(contract, "callput")) ? "Call" :
      /^(put|p)$/i.test(get(contract, "callput")) ? "Put" : null;
    if (!symbol || !validDate(expiration) || strike === null || strike <= 0 || !right) throw invalidPayload();
    const occ = /^@([A-Z0-9.$^/ -]{1,16})(\d{6})([CP])(\d{8})(?::(?:US|CA))?$/i.exec(symbol);
    if (!occ || `20${occ[2].slice(0, 2)}-${occ[2].slice(2, 4)}-${occ[2].slice(4)}` !== expiration ||
        (occ[3].toUpperCase() === "C" ? "Call" : "Put") !== right || Number(occ[4]) / 1000 !== strike) throw invalidPayload();
    checkProviderError(root);
    checkEntitlement(get(item, "entitlement"), availability);
    checkEntitlement(get(root, "entitlement"), availability);
    const firstNumber = name => [greeksSource, contract, price, item].map(value => numberOf(value, name))
      .find(value => value !== null) ?? null;
    const greeks = Object.fromEntries(["delta", "gamma", "theta", "vega", "rho"].map(name => [name, firstNumber(name)]));
    const bid = numberOf(price, "bid"), ask = numberOf(price, "ask"), last = numberOf(price, "last");
    return { symbol, expiration, strike, right, underlyingSymbol: symbolOf(root),
      exchangeCode: get(item, "exchange") || get(get(item, "key"), "exchange") || "OPRAC",
      contractType: get(contract, "type"),
      quote: { bid, ask, last, bidSize: integerOf(price, "bidsize"), askSize: integerOf(price, "asksize"),
        change: numberOf(price, "change"), changePercent: numberOf(price, "changepercent"),
        dayHigh: numberOf(price, "high"), dayLow: numberOf(price, "low"),
        volume: integerOf(price, "contractvolume"), openInterest: integerOf(contract, "openinterest"),
        impliedVolatility: firstNumber("impvol") ?? firstNumber("impliedvolatility"),
        greeks: Object.values(greeks).every(value => value === null) ? null : greeks,
        intrinsicValue: firstNumber("intrinsicvalue"), extrinsicValue: firstNumber("extrinsicvalue"), breakEvenPrice: null,
        quoteTimestampUtc: timestampOf(price, "lastquotedatetime") || timestampOf(price, "lasttradeddatetime") || timestampOf(item, "datetime"),
        state: bid === null && ask === null && last === null ? "Unavailable" : stateOf(get(item, "entitlement")) } };
  });
}

export function parseUnderlyingQuote(payload, grant) {
  const requested = grant.underlying.instrument.providerSymbol;
  const entries = quoteElements(resultsOf(payload));
  if (entries.length !== 1 || !sameSymbol(symbolOf(entries[0]), requested)) throw invalidPayload();
  const entry = entries[0];
  checkProviderError(entry);
  const entitlement = get(entry, "entitlement");
  checkEntitlement(entitlement, grant.availability);
  const price = get(entry, "pricedata");
  const last = numberOf(price, "last");
  return { ...grant.underlying, last, change: numberOf(price, "change"),
    changePercent: numberOf(price, "changepercent"),
    quoteTimestampUtc: timestampOf(price, "lastquotedatetime") || timestampOf(price, "lasttradedatetime")
      || timestampOf(entry, "datetime"),
    quoteState: last === null ? "Unavailable" : stateOf(entitlement) };
}

export function optionExpirations(snapshot, grant) {
  const today = exchangeDate(grant);
  return [...new Map(snapshot.contracts.filter(item => item.expiration >= today)
    .map(item => [item.expiration, expirationInfo(item.expiration, item.contractType, today)])).values()]
    .sort((left, right) => left.date.localeCompare(right.date));
}

export function selectOptionStrikes(contracts, request, atTheMoney) {
  if (!contracts.length) return [];
  const strikes = [...new Set(contracts.map(item => item.strike))].sort((a, b) => a - b);
  if (request.strikeRange === "All") return contracts;
  if (request.strikeRange === "Custom") {
    if (!(request.minimumStrike > 0) || !(request.maximumStrike >= request.minimumStrike)) throw invalidPayload();
    return contracts.filter(item => item.strike >= request.minimumStrike && item.strike <= request.maximumStrike);
  }
  if (atTheMoney === null) throw optionError("QuoteMedia did not identify an at-the-money strike.", 502);
  const radius = request.strikeRange === "NearTheMoney" ? 3 : Number(request.strikes ?? 10);
  if (!Number.isInteger(radius) || radius < 1 || radius > 24) throw invalidPayload();
  const index = strikes.indexOf(atTheMoney);
  const selected = new Set(strikes.slice(Math.max(0, index - radius), index + radius + 1));
  return contracts.filter(item => selected.has(item.strike));
}

export function buildBrowserOptionChain(snapshot, selected, details, identities, request, grant, atTheMoney) {
  const quotes = new Map(details.map(item => [item.symbol, item]));
  const contracts = new Map(identities.map(item => [item.canonicalSymbol, item]));
  const rows = new Map();
  for (const item of selected) {
    const detail = quotes.get(item.symbol) || item;
    if (detail.expiration !== item.expiration || detail.right !== item.right || detail.strike !== item.strike ||
        detail.underlyingSymbol && !sameSymbol(detail.underlyingSymbol, grant.underlying.instrument.providerSymbol)) throw invalidPayload();
    const contract = contracts.get(item.symbol);
    if (!contract || contract.expiration !== item.expiration || contract.strike !== item.strike || contract.right !== item.right ||
        contract.underlyingInstrumentId !== request.underlyingInstrumentId) throw invalidPayload();
    const spot = snapshot.underlying.last;
    const intrinsicValue = detail.quote.intrinsicValue ?? (spot === null ? null : Math.max(0,
      item.right === "Call" ? spot - item.strike : item.strike - spot));
    const quote = { ...detail.quote, intrinsicValue,
      extrinsicValue: detail.quote.extrinsicValue ?? (intrinsicValue === null || detail.quote.last === null ? null :
        Math.max(0, detail.quote.last - intrinsicValue)),
      breakEvenPrice: detail.quote.last === null ? null :
        item.strike + (item.right === "Call" ? detail.quote.last : -detail.quote.last) };
    if (!rows.has(item.strike)) rows.set(item.strike, { strike: item.strike, isAtTheMoney: item.strike === atTheMoney,
      callIsInTheMoney: spot !== null && item.strike < spot, putIsInTheMoney: spot !== null && item.strike > spot,
      call: null, put: null });
    rows.get(item.strike)[item.right.toLowerCase()] = { contract, quote };
  }
  return { underlying: snapshot.underlying,
    expiration: expirationInfo(request.expiration, selected[0]?.contractType, exchangeDate(grant)),
    rows: [...rows.values()].sort((a, b) => a.strike - b.strike), atTheMoneyStrike: atTheMoney,
    generatedAtUtc: new Date().toISOString(), source: "QuoteMedia", isDevelopmentData: false };
}

function get(value, key) {
  if (!value || typeof value !== "object") return undefined;
  return Object.entries(value).find(([name]) => name.toLowerCase() === key.toLowerCase())?.[1];
}
function symbolOf(value) {
  const symbol = get(value, "symbolstring") ?? get(value, "symbol") ?? get(get(value, "key"), "symbol");
  return typeof symbol === "string" ? symbol.trim() : null;
}
function sameSymbol(a, b) { return typeof a === "string" && typeof b === "string" && a.trim().toUpperCase() === b.trim().toUpperCase(); }
function numberOf(value, key) {
  const raw = get(value, key);
  if (typeof raw !== "number" && typeof raw !== "string" || typeof raw === "string" && !raw.trim()) return null;
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
}
function integerOf(value, key) { const number = numberOf(value, key); return Number.isSafeInteger(number) && number >= 0 ? number : null; }
function timestampOf(value, key) {
  const text = get(value, key);
  return typeof text === "string" && /(Z|[+-]\d\d:\d\d)$/i.test(text) && Number.isFinite(Date.parse(text)) ? new Date(text).toISOString() : null;
}
function validDate(value) { return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value; }
function stateOf(value) { return ({ RT: "RealTime", "RT+": "RealTime", DL: "Delayed", "DL+": "Delayed", EOD: "Stale" })[String(value || "").trim().toUpperCase()] || "Unavailable"; }
function checkEntitlement(value, availability) {
  if (value === undefined || value === null || value === "") return;
  const entitlement = String(value).trim().toUpperCase();
  if (!(entitlement === "EOD" || ["DL", "DL+"].includes(entitlement) && availability !== "Historical" ||
      ["RT", "RT+"].includes(entitlement) && availability === "RealTime")) throw invalidPayload();
}
function checkProviderError(value) {
  const code = get(value, "code");
  const status = Number(typeof code === "object" ? get(code, "value") : code);
  if (code !== undefined && (!Number.isFinite(status) || status !== 0)) throw optionError("QuoteMedia rejected the options request.", [401, 403].includes(status) ? status : 502,
    [401, 403].includes(status) ? "provider_auth_rejected" : null);
  for (const key of ["error", "errors"]) {
    const error = get(value, key);
    if (error && (typeof error !== "object" || Object.keys(error).length)) throw invalidPayload();
  }
}
function resultsOf(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) throw invalidPayload();
  checkProviderError(payload);
  const results = get(payload, "results") ?? payload;
  if (!results || typeof results !== "object" || Array.isArray(results)) throw invalidPayload();
  checkProviderError(results);
  return results;
}
function quoteElements(results) {
  const chain = get(results, "optionchain");
  checkProviderError(chain);
  const elements = get(results, "quotedata") ?? get(results, "quote") ?? get(chain, "quotedata") ?? get(chain, "quote");
  if (elements === undefined || elements === null) {
    if (!chain && Number(get(results, "symbolcount")) !== 0) throw invalidPayload();
    return [];
  }
  return (Array.isArray(elements) ? elements : [elements]).flatMap(item => {
    const symbols = get(item, "symbolstring") ?? get(item, "symbol");
    if (!Array.isArray(symbols)) return [item];
    // getOptions can group a batch into parallel top-level arrays, unlike
    // getOptionChain's individual quote objects. Keep every field aligned.
    if (!symbols.length || Object.values(item).some(value => Array.isArray(value) && value.length !== symbols.length)) throw invalidPayload();
    return symbols.map((_, index) => Object.fromEntries(Object.entries(item)
      .map(([key, value]) => [key, Array.isArray(value) ? value[index] : value])));
  });
}
function exchangeDate(grant) {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: grant.underlying.instrument.timeZoneId || "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(grant.serverTimeUtc));
  const field = name => parts.find(item => item.type === name).value;
  return `${field("year")}-${field("month")}-${field("day")}`;
}
function expirationInfo(date, type, today) {
  return { date, daysToExpiration: Math.round((Date.parse(date) - Date.parse(today)) / 86400000),
    classification: ({ WEEK: "Weekly", STAN: "Monthly", QUART: "Quarterly" })[String(type).toUpperCase()] || "Standard", isAvailable: true };
}
export function optionError(message, status, reason = null) {
  return Object.assign(new Error(message), { name: "OptionChainRequestError", status, reason });
}
function invalidPayload() { return optionError("QuoteMedia did not return usable options data.", 502); }
